import { useEffect } from "react";
import { isColumnPrompted } from "@/domains/matrix";
import type { MatrixColumnDef, MatrixRowUi } from "@/domains/matrix";
import type { CellAddress } from "../types";

const ARROW_KEYS = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]);

function clamp(value: number, max: number): number {
  return Math.min(Math.max(value, 0), max);
}

type GridKeyboardNavigationParams = {
  /** In the order shown on screen: sorting drives what the keyboard walks. */
  orderedRows: MatrixRowUi[];
  columns: MatrixColumnDef[];
  selectedCell: CellAddress | null;
  editingCell: CellAddress | null;
  onSelectCell: (address: CellAddress | null) => void;
  onStartEdit: (address: CellAddress) => void;
  onCancelEdit: () => void;
  onExpandCell: (address: CellAddress) => void;
};

/**
 * Walks the grid from a document-level key handler, so every cell action is
 * reachable without a pointer.
 *
 * Escape clears the selection or the editor, Enter acts on the selected cell,
 * Tab runs along the row and wraps at either end, and the arrows move the
 * selection. Arrows are ignored while editing because the caret needs them.
 */
export function useGridKeyboardNavigation({
  orderedRows,
  columns,
  selectedCell,
  editingCell,
  onSelectCell,
  onStartEdit,
  onCancelEdit,
  onExpandCell,
}: GridKeyboardNavigationParams) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (editingCell) {
          onCancelEdit();
        } else if (selectedCell) {
          onSelectCell(null);
        }
      }
      if (e.key === "Enter" && selectedCell && !editingCell) {
        // Prompt columns are read-only, so Enter opens the value instead.
        const column = columns.find((c) => c.key === selectedCell.columnKey);
        if (column && isColumnPrompted(column)) {
          onExpandCell(selectedCell);
        } else {
          onStartEdit(selectedCell);
        }
      }

      // Handled here too, so a run of Tabs carries on across read-only columns.
      if (e.key === "Tab" && selectedCell && !editingCell) {
        const rowIndex = orderedRows.findIndex(
          (row) => row.analysisId === selectedCell.analysisId,
        );
        const columnIndex = columns.findIndex(
          (column) => column.key === selectedCell.columnKey,
        );
        if (rowIndex === -1 || columnIndex === -1) return;

        let nextColumnIndex = columnIndex + (e.shiftKey ? -1 : 1);
        let nextRowIndex = rowIndex;
        if (nextColumnIndex >= columns.length) {
          nextColumnIndex = 0;
          nextRowIndex += 1;
        } else if (nextColumnIndex < 0) {
          nextColumnIndex = columns.length - 1;
          nextRowIndex -= 1;
        }

        const nextRow = orderedRows[nextRowIndex];
        const nextColumn = columns[nextColumnIndex];
        if (!nextRow || !nextColumn) return;
        e.preventDefault();
        const nextAddress = {
          analysisId: nextRow.analysisId,
          columnKey: nextColumn.key,
        };
        onSelectCell(nextAddress);
        if (!isColumnPrompted(nextColumn)) onStartEdit(nextAddress);
        return;
      }

      // Never while editing: the caret needs the arrow keys.
      if (editingCell || !ARROW_KEYS.has(e.key)) return;
      const rowIndex = orderedRows.findIndex(
        (row) => row.analysisId === selectedCell?.analysisId,
      );
      const columnIndex = columns.findIndex(
        (column) => column.key === selectedCell?.columnKey,
      );
      if (rowIndex === -1 || columnIndex === -1) {
        const firstRow = orderedRows[0];
        const firstColumn = columns[0];
        if (firstRow && firstColumn) {
          e.preventDefault();
          onSelectCell({
            analysisId: firstRow.analysisId,
            columnKey: firstColumn.key,
          });
        }
        return;
      }

      const nextRowIndex = clamp(
        rowIndex + (e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0),
        orderedRows.length - 1,
      );
      const nextColumnIndex = clamp(
        columnIndex +
          (e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0),
        columns.length - 1,
      );
      const nextRow = orderedRows[nextRowIndex];
      const nextColumn = columns[nextColumnIndex];
      if (!nextRow || !nextColumn) return;
      e.preventDefault();
      onSelectCell({
        analysisId: nextRow.analysisId,
        columnKey: nextColumn.key,
      });
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    columns,
    orderedRows,
    selectedCell,
    editingCell,
    onSelectCell,
    onStartEdit,
    onCancelEdit,
    onExpandCell,
  ]);
}
